import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import { IElementProperty } from '../element-property.model';
import type { ImportRowDraft } from '../element-import.model';
import { ProductRecord } from '../product.model';
import { ResultsRecord } from '../unit.model';
import { IProposal } from '../proposals.model';
import type { ReportSlideKind } from '../report-slides.model';
import { ElementKind } from './element-kind.model';
import {
  IBaseElement,
  IElement,
  OneOfElementListChildren,
} from './element.model';

/** Default display name for a newly created {@link Baseline}. */
export const DEFAULT_BASELINE_NAME = 'Baseline';

/** Whether a candidate row was discarded by the user or by an automatic rule. */
export type DiscardTrigger = 'user' | 'auto';

/**
 * A candidate row's materialized element, stashed when the row is discarded so
 * restore is lossless (DEV-3357). The element's ids are NOT authoritative — on
 * restore the element is re-added with freshly regenerated ids, so this stash
 * never participates in version-duplication idMap remapping (see {@link Baseline.imports}).
 */
export type StashedElement = {
  element: IElement;
  /** Parent the element was removed from; falls back to the version root if stale. */
  parentId: string;
};

/** Discard record for a single candidate row (DEV-3357). */
export type DiscardData = {
  type: DiscardTrigger;
  /** Removed element, when the row was materialized; absent → restore yields INCOMPLETE. */
  stashed?: StashedElement;
};

/**
 * CSV element-import state for a version (DEV-3298 / DEV-3357). Holds DB ids,
 * not live element ids. A new version does not receive this state. When a
 * factory is cloned with it, stashed {@link DiscardData} parent ids are
 * remapped; the stashed element's own ids stay non-authoritative and are
 * regenerated on restore.
 */
export type VersionImports = {
  importIds: string[];
  /** Keyed by candidate-row id; key present ⇒ discarded. */
  discardedRows: Partial<Record<string, DiscardData>>;
  /**
   * Unsaved per-candidate-row mapping drafts (DEV-3368), keyed by candidate-row
   * id. Holds pre-materialization mapping intent so it syncs across
   * users/devices and is captured by undo/redo. Absent ⇒ no draft for that row.
   */
  draftsByRowId?: Partial<Record<string, ImportRowDraft>>;
};

/**
 * Element-tree root for a project (unmarked scenario plus nested proposals).
 * Formerly `IBuildingVersion`. Stored on {@link Project.baselines}.
 *
 * After the DEV-3491 recipe-library DB migration, recipe composition lives on
 * host {@link Baseline.elements} (materialized children). A legacy `recipes`
 * catalog on the baseline JSON (`baselines[].recipes`, formerly
 * `buildings[].versions[].recipes`) is forbidden — project validation throws
 * `VERSION_HAS_EMBEDDED_RECIPES` if that own key is present (DEV-3701). That
 * check assumes the migration has already run.
 */
export interface Baseline extends IBaseElement {
  name: string;
  kind: ElementKind.Version;
  /** User defined SBEF group names keyed by group key (e.g. "A5.1"). */
  sbefGroupNames?: Partial<Record<string, string>>;

  imports?: VersionImports;

  /**
   * All products used in this {@link Baseline}.
   * Note that ALL products from ALL proposals must be included here as well.
   */
  products: ProductRecord;

  /**
   * List of elements.
   * Basically what's shown in Element List
   */
  elements: OneOfElementListChildren[];

  // TODO: Make sure this always exist by migrating DB
  properties?: IElementProperty[];

  /**
   * Action-backed proposals for this version.
   */
  proposals: IProposal[];

  /**
   * Actions referenced by {@link IProposal}.
   */
  actions?: Action[];

  /**
   * Results record for this {@link Baseline}.
   * The rollup for the unmarked scenario is keyed by this baseline's id.
   */
  resultsRecord: ResultsRecord;

  report_summary_col1?: string;
  report_summary_col2?: string;
  /**
   * Optional title override on the summary report page.
   * When unset, {@link REPORT_SUMMARY_DEFAULT_TITLE} is shown as placeholder text.
   */
  report_summary_title?: string;
  /**
   * Optional title override on the baseline report dashboard page.
   * When unset, {@link Baseline.name} is shown as placeholder text.
   * @deprecated Legacy shared field — prefer {@link report_product_groups_title}.
   */
  report_title?: string;
  /**
   * Optional body text on the baseline report dashboard page.
   * When unset, the report body placeholder is shown instead.
   * @deprecated Legacy shared field — prefer {@link report_product_groups_description}.
   */
  report_description?: string;
  /** Hero title on the product-groups report slide. */
  report_product_groups_title?: string;
  /** Hero body on the product-groups report slide. */
  report_product_groups_description?: string;
  /** Hero title on the high-carbon products report slide. */
  report_top_products_title?: string;
  /** Hero body on the high-carbon products report slide. */
  report_top_products_description?: string;
  /** Hero title on the actions report slide. */
  report_actions_title?: string;
  /** Hero body on the actions report slide. */
  report_actions_description?: string;
  /**
   * Hidden report slides. Shared kinds (`cover`, `summary`, `methodology`)
   * apply to the whole report; proposal kinds apply to the baseline pages.
   */
  report_hidden_slides?: ReportSlideKind[];
}
